import { useCallback } from "react";
import { CheckCircle, Shirt, Trash2, Copy } from "lucide-react";
import type { Side } from "@design-lab/shared";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { useTabNavigation } from "@design-lab/shared/hooks/useTabNavigation";
import { Button, SpaceHolder } from "@design-lab/shared/ui";
import { useArtworkSelection } from "@design-lab/features/weave-design-lab/model/useArtworkSelection";
import { useArtworkImage } from "../model/useArtworkImage";
import { useEnhancedImageSource } from "../model/useEnhancedImageSource";
import { useArtworkCopy } from "../model/useArtworkCopy";

type ImageUploadedBannerProps = {
  image: { url?: string | undefined; file?: File | undefined };
  side: Side;
};

export function ImageUploadedBanner(props: ImageUploadedBannerProps) {
  const { image, side } = props;
  const { isDesktop } = useResponsive();
  const { isDesignTab } = useTabNavigation();
  const { deleteArtwork } = useArtworkImage();
  const { artworkId } = useEnhancedImageSource({ side });
  const { clearSelection } = useArtworkSelection();
  const { copyArtworkToOtherSide, isShowingCopySuccess } = useArtworkCopy(side);

  const onArtworkRemove = useCallback(() => {
    deleteArtwork(side, artworkId);
    clearSelection(side);
  }, [deleteArtwork, side, artworkId, clearSelection]);

  const onCopyToOtherSide = useCallback(() => {
    if (isShowingCopySuccess) return;
    try {
      copyArtworkToOtherSide();
    } catch (error) {
      window.Honeybadger?.notify(`[Design Lab] Failed to copy artwork`, {
        context: {
          error: error instanceof Error ? error.message : String(error),
          error_object: error instanceof Error ? error.toString() : String(error),
          stack_trace: error instanceof Error ? error.stack : null,
          component: "ImageUploadedBanner",
          action: "copyArtworkToOtherSide",
          side,
          artworkId
        }
      });
    }
  }, [copyArtworkToOtherSide, side, artworkId, isShowingCopySuccess]);

  const shirt = (
    <div className="flex items-center gap-2">
      <Shirt className="size-6" />
      <p className="font-bold">
        <span className="capitalize">{side}</span> of Shirt
      </p>
    </div>
  );

  return (
    <div className="flex flex-1 items-center gap-2 p-4 lg:flex-col lg:justify-center lg:p-1">
      {isDesktop && shirt}
      {image.url ? (
        <img src={image.url} alt="Uploaded image" className="size-18 lg:size-22 object-contain" />
      ) : (
        <div className="flex h-16 w-16 items-center justify-center rounded-sm bg-gray-200 p-1 text-sm uppercase text-gray-500 lg:h-20 lg:w-20">
          {image.file?.type?.split("/")?.pop()}
        </div>
      )}

      <div className="space-y-2">
        {!isDesktop && shirt}
        <div className="flex w-full items-center gap-2">
          <CheckCircle className="h-6 w-6 text-blue-600" />
          <p className="text-sm text-gray-900">Image uploaded successfully</p>
        </div>
        <div className="flex flex-col gap-2 lg:flex-row lg:justify-center">
          {!isDesignTab && (
            <Button
              data-testid="copy-to-other-side-button"
              variant="outline"
              size="sm"
              onPress={onCopyToOtherSide}
              isDisabled={isShowingCopySuccess}
            >
              <SpaceHolder>
                <SpaceHolder.Item
                  visible={isShowingCopySuccess}
                  className="flex items-center gap-2"
                >
                  <CheckCircle className="size-4 text-blue-600" />
                  Copied
                </SpaceHolder.Item>
                <SpaceHolder.Item
                  visible={!isShowingCopySuccess}
                  className="flex items-center gap-2"
                >
                  <Copy className="size-4 text-blue-600" />
                  Copy to Other Side
                </SpaceHolder.Item>
              </SpaceHolder>
            </Button>
          )}
          <Button
            data-testid="delete-image-button"
            variant="outline"
            size="sm"
            onPress={onArtworkRemove}
          >
            <Trash2 className="size-4 text-red-600" />
            Delete image
          </Button>
        </div>
      </div>
    </div>
  );
}
